import { useEffect, useState } from 'react'
import { RGBColor } from 'react-color'
import toast, { Toaster } from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ModuleWindow } from '../../components/moduleWindow/ModuleWindow'
import { TuningInformationCard } from '../../components/tuning/tuningInformationCard/TuningInformationCard'
import { VinylInformationCard } from '../../components/vinyls/vinylInformationCard/VinylInformationCard'
import { MoveZone } from '../../components/zone/moveZone/MoveZone'
import { translations } from '../../configs/translations'
import { RaceTypes } from '../../enums/RaceTypes'
import {
	TuningDto,
	TuningItemDto,
	TuningTypeDto,
	TuningWraperDto,
} from '../../models/TuningDto'
import { UserProfile } from '../../models/UserProfile'
import { VehicleService } from '../../services/VehicleService'
import { VinylService } from '../../services/VinylService'
import { useStore } from '../../store/store'
import { formatTextWithParams } from '../../utils/formatTextWithParams'
import { Header } from '../main/header/Header'
import { TuningRoulette } from './roulette/TuningRoulette'
import './Tuning.css'

export const Tuning = () => {
	const [userProfile, setUserProfile] = useState<UserProfile | null>(JSON.parse(localStorage.getItem('profile') || 'null'))
	const language = useStore(
		state => state.language
	) as keyof typeof translations
	const t = translations[language]

	const [searchParams] = useSearchParams()
	const carId = Number(searchParams.get('carId')) || Number(localStorage.getItem('lastVehicleId'))

	const [currentState, setCurrentState] = useState<
		'parts' | 'types' | 'tuning'
	>('parts')
	const [tuning, setTuning] = useState<TuningWraperDto | null>(null)

	const [selectedPart, setSelectedPart] = useState<TuningDto | null>(null)
	const [selectedPartId, setSelectedPartId] = useState<number | null>(null)
	const [selectedType, setSelectedType] = useState<TuningTypeDto | null>(null)
	const [selectedTuning, setSelectedTuning] = useState<{
		id: number
		name: string
	} | null>(null)
	const [selectedTuningPrice, setSelectedTuningPrice] = useState<number | null>(
		null
	)
	const [selectedTuningIsEquipped, setSelectedTuningIsEquipped] = useState<
		boolean | null
	>(null)
	const [selectedTuningIsBought, setSelectedTuningIsBought] = useState<
		boolean | null
	>(null)
	const [selectedTuningIsDonate, setSelectedTuningIsDonate] = useState(false)
	const [selectedTuningRequired, setSelectedTuningRequired] = useState<string[]>([])
	const [selectedTuningConflict, setSelectedTuningConflict] = useState<string[]>([])
	const [isColorRgb, setIsColorRgb] = useState<boolean>(false)

	const [selecteTuningRequestSent, setSelectedTuningRequestSent] =
		useState(false)

	const getTuningName = (
		part: TuningDto,
		items: TuningItemDto[],
		index: number
	): string =>
		t[items[index].name as keyof typeof t] ??
		t[part.name?.toLowerCase() as keyof typeof t] + ' ' + index

	const getTuningNameById = (id: string): string => {
		for (const part of tuning?.tuning ?? []) {
			const lists = [
				part.availableTunings,
				...part.availableTypes.map(type => type.availableTunings),
			]
			for (const items of lists) {
				const index = items.findIndex(item => item.name === id)
				if (index !== -1) return getTuningName(part, items, index)
			}
		}
		return id
	}

	const showError = (error: any) => {
		const data: string | undefined = error?.response?.data
		let message: string = t.error_unknown
		const match =
			typeof data === 'string' &&
			data.match(/^tuning_(conflict|required)_(.+)$/)
		if (match) {
			const key = match[1] === 'conflict' ? 'tuning_conflict' : 'tuning_required'
			message = `${t[key]} ${getTuningNameById(match[2])}`
		} else if (data && t[data as keyof typeof t]) {
			message = t[data as keyof typeof t]
		}
		toast.error(message, { className: 'notification-error' })
	}

	const fetchSelectedTuningInfo = async (
		carId: number,
		partId: number,
		tuningId: number,
		typeId: number
	) => {
		await VehicleService.getSelectedTuningInfo(carId, partId, tuningId, typeId)
			.then(response => {
				setSelectedTuningPrice(response.price)
				setSelectedTuningIsEquipped(response.equipped)
				setSelectedTuningIsBought(response.bought)
				setSelectedTuningIsDonate(response.isDonate ?? false)
				setSelectedTuningRequired(response.required ?? [])
				setSelectedTuningConflict(response.conflict ?? [])
			})
			.catch(error => {
				showError(error)
			})
	}

	const setTuningInGame = async (tuningId: number) => {
		await VehicleService.setTuning(
			carId,
			selectedPart?.partId ?? 0,
			tuningId,
			selectedType?.gameId ?? -100
		).catch(error => {
			showError(error)
		})
	}

	const onSelect = (tuningId: number, tuningName: string) => {
		const gameId = tuningId
		if (selecteTuningRequestSent) return
		setSelectedTuningRequestSent(true)
		fetchSelectedTuningInfo(
			carId,
			selectedPart?.partId ?? 0,
			gameId,
			selectedType?.gameId ?? -100
		)
		setSelectedTuning({ id: gameId, name: tuningName })
		setTuningInGame(gameId)

		const requestTimeOut = isColorRgb ? 100 : 25
		setTimeout(() => {
			setSelectedTuningRequestSent(false)
		}, requestTimeOut)
	}

	const onPartSelect = (part: {
		id: number
		name: string
		image: string
		areAvailableTypes: boolean
		part: TuningDto
	}) => {
		const CHIP_TUNING_PART_ID = 999
		if (part.id === CHIP_TUNING_PART_ID) {
			navigate('/fine-tuning?vehicleId=' + carId)
			return
		}
		setSelectedPart(part.part)
		setSelectedPartId(part.part.partId)
		if (part.areAvailableTypes) {
			setCurrentState('types')
		} else {
			setCurrentState('tuning')
		}
	}

	const getRoulette = () => {
		if (currentState === 'types') {
			let types: {
				id: number
				name: string
				image: string
				type: TuningTypeDto
			}[] = []

			if (!tuning) return <TuningRoulette items={[]} selectedVehicle={null} />

			selectedPart?.availableTypes.forEach(type => {
				types.push({
					id: type.gameId,
					name: t[type.name.toLowerCase() as keyof typeof t],
					image: selectedPart?.name?.toLowerCase() ?? '',
					type: type,
				})
			})

			return (
				<TuningRoulette
					onBack={() => {
						setSelectedTuning(null)
						setCurrentState('parts')
					}}
					selectedVehicle={null}
					items={types.map(type => ({
						name: type.name,
						image: type.image,
						onClick: () => {
							setSelectedType(type.type)
							setCurrentState('tuning')
						},
					}))}
				/>
			)
		} else if (currentState === 'tuning') {
			const image = selectedPart?.name?.toLowerCase() ?? ''
			const items =
				(selectedType ?? selectedPart)?.availableTunings ?? []
			const tunings = selectedPart
				? items.map((item, k) => ({
					item,
					name: getTuningName(selectedPart, items, k),
					image,
				}))
				: []

			return (
				<TuningRoulette
					onBack={() => {
						setSelectedType(null)
						setSelectedTuning(null)
						setCurrentState(selectedPart?.availableTypes?.length ?? 0 > 0 ? 'types' : 'parts')
					}}
					selectedVehicle={null}
					items={tunings.map(tuning => ({
						name: tuning.name,
						image: tuning.image,
						isDonate: tuning.item.isDonate,
						onClick: () => {
							onSelect(tuning.item.tuningId, tuning.name)
						},
					}))}
				/>
			)
		} else {
			let parts: {
				id: number
				name: string
				image: string
				areAvailableTypes: boolean
				part: TuningDto
			}[] = []

			if (!tuning) return <TuningRoulette items={[]} selectedVehicle={null} />

			const tuningParts = tuning.tuning as TuningDto[]
			tuningParts.forEach(tuning => {
				const id = tuning.partId
				if (parts.find(part => part.id === id)) return

				const toSkip = [
					'engine',
					'brakes',
					'turbo',
					'armor',
					'transmission',
					'tyres',
				]
				if (toSkip.includes(tuning.name?.toLowerCase() ?? '')) return

				parts.push({
					id: id,
					name: t[tuning.name?.toLowerCase() as keyof typeof t] ?? '',
					image: tuning.name?.toLowerCase() ?? '',
					areAvailableTypes: tuning.availableTypes.length > 0,
					part: tuning,
				})
			})


			return (
				<TuningRoulette
					selectedVehicle={null}
					items={parts.map(part => ({
						name: part.name,
						image: part.image,
						onClick: () => onPartSelect(part),
					}))}
				/>
			)
		}
	}

	const fetchTuning = async () => {
		await VehicleService.getAvailableTunings(carId)
			.then(response => {
				setTuning(response)
			})
			.catch(error => {
				showError(error)
			})
	}

	useEffect(() => {
		fetchTuning()
	}, [])

	const buySound = new Audio('/sounds/buy.mp3')
	const [isBuyConfirmOpen, setIsBuyConfirmOpen] = useState(false)
	const getBuyConfirmText = () => {
		const price = `${selectedTuningPrice ?? 0}${selectedTuningIsDonate ? ' DP' : '$'}`
		const conflict = selectedTuningConflict.map(getTuningNameById)
		const required = selectedTuningRequired.map(getTuningNameById)
		return [
			formatTextWithParams(t.buy_tuning_desc, [selectedTuning?.name ?? '', price]),
			conflict.length ? `${t.tuning_conflict_list}: ${conflict.join(', ')}` : '',
			required.length ? `${t.tuning_required_list}: ${required.join(', ')}` : '',
		].filter(Boolean).join('\n')
	}

	const handleBuy = async () => {
		setIsBuyConfirmOpen(false)
		await VehicleService.buyTuning(
			carId,
			selectedPart?.partId ?? 0,
			selectedTuning?.id ?? 0,
			selectedType?.gameId ?? -100
		)
			.then(() => {
				buySound.play()
				fetchSelectedTuningInfo(
					carId,
					selectedPart?.partId ?? 0,
					selectedTuning?.id ?? 0,
					selectedType?.gameId ?? -100
				)
				setUserProfile(null)
			})
			.catch(error => {
				showError(error)
			})
	}

	const handleEquip = async () => {
		await VehicleService.equipTuning(
			carId,
			selectedPart?.partId ?? 0,
			selectedTuning?.id ?? 0,
			selectedType?.gameId ?? -100
		)
			.then(() => {
				fetchSelectedTuningInfo(
					carId,
					selectedPart?.partId ?? 0,
					selectedTuning?.id ?? 0,
					selectedType?.gameId ?? -100
				)
			})
			.catch(error => {
				showError(error)
			})
	}

	const [color, setColor] = useState<RGBColor>({ r: 0, g: 0, b: 0 })
	const [isVinyls, setIsVinyls] = useState(false)

	useEffect(() => {
		// rgb colors

		if (
			(selectedPartId == 101 || selectedPartId == 102) &&
			selectedType?.gameId != null
		) {
			fetchSelectedTuningInfo(carId, selectedPartId, 0, selectedType?.gameId)
				.then(() => {
					setIsColorRgb(true)
				})
		} else {
			setIsColorRgb(false)
		}

		// vinyls
		if (selectedPart?.partId == 103) {
			setIsVinyls(true)
		} else {
			setIsVinyls(false)
		}
	}, [selectedPartId, selectedType])

	const handleColorBuy = async () => {
		const colorTuningId = parseInt(
			'1' +
			color.r.toString().padStart(3, '0') +
			color.g.toString().padStart(3, '0') +
			color.b.toString().padStart(3, '0')
		)
		await VehicleService.buyTuning(
			carId,
			selectedPart?.partId ?? 0,
			colorTuningId,
			selectedType?.gameId ?? -1
		)
			.then(() => {
				buySound.play()
				fetchSelectedTuningInfo(
					carId,
					selectedPart?.partId ?? 0,
					0,
					selectedType?.gameId ?? -1
				)
				setUserProfile(null)
			})
			.catch(error => {
				showError(error)
			})
	}

	const [isColorRequestSent, setIsColorRequestSent] = useState(false)
	useEffect(() => {
		if (isColorRgb && !isColorRequestSent) {
			const colorTuningId = parseInt(
				'1' +
				color.r.toString().padStart(3, '0') +
				color.g.toString().padStart(3, '0') +
				color.b.toString().padStart(3, '0')
			)
			onSelect(colorTuningId, 'color')
			setIsColorRequestSent(true)
			setTimeout(() => {
				setIsColorRequestSent(false)
			}, 100)
		}
	}, [color])

	const navigate = useNavigate()
	const handleOpenVinylEditor = (mode: string) => {
		navigate(
			`/vinyls?carId=${carId}&mode=${mode}&vinylLocalId=${selectedPartId}`
		)
	}

	const handleEquipVinyl = async () => {
		await VinylService.equipVinyl(carId, selectedTuning?.id ?? 0)
			.then(() => {
				setSelectedTuningIsEquipped(true)
				setTimeout(() => {
					setSelectedTuningIsEquipped(false)
				}, 5000)
			})
			.catch(error => {
				showError(error)
			})
	}

	const [isRequestSetVinylSend, setIsRequestSetVinylSend] = useState(false)
	const getTuningInformationCard = () => {
		if (isColorRgb) {
			return (
				<TuningInformationCard
					title={'Color'}
					price={selectedTuningPrice}
					isEquipped={false}
					isBought={false}
					showPalette={true}
					handleBuy={handleColorBuy}
					handleEquip={() => { }}
					color={color}
					setColor={setColor}
				/>
			)
		}

		if (isVinyls) {
			if (selectedTuning?.id === -1) {
				return (
					<VinylInformationCard
						title={t.create_vinyl}
						isEquipped={selectedTuningIsEquipped ?? false}
						handleCreate={handleOpenVinylEditor}
					/>
				)
			} else if (selectedTuning) {
				return (
					<VinylInformationCard
						title={selectedTuning?.name ?? 'Unknown'}
						isEquipped={selectedTuningIsEquipped ?? false}
						handleEquip={handleEquipVinyl}
						handleEdit={handleOpenVinylEditor}
					/>
				)
			}
		}

		if (selectedTuning) {
			return (
				<TuningInformationCard
					title={selectedTuning?.name ?? 'Unknown'}
					price={selectedTuningPrice ?? 0}
					isDonate={selectedTuningIsDonate}
					required={selectedTuningRequired.map(getTuningNameById)}
					conflict={selectedTuningConflict.map(getTuningNameById)}
					isEquipped={selectedTuningIsEquipped ?? false}
					isBought={
						(selectedTuningIsBought ?? false) || selectedTuning.id == -1
					}
					handleBuy={() => setIsBuyConfirmOpen(true)}
					handleEquip={handleEquip}
				/>
			)
		}
	}

	useEffect(() => {
		if (isVinyls && selectedTuning) {
			const sendRequestSetVinyl = async () => {
				setIsRequestSetVinylSend(true)
				await VinylService.setMenuVinyl(carId, selectedTuning.id).catch(
					error => {
						showError(error)
					}
				)
				setTimeout(() => {
					setIsRequestSetVinylSend(false)
				}, 250)
			}

			if (!isRequestSetVinylSend) {
				sendRequestSetVinyl()
			}
		}
	}, [selectedTuning, isVinyls])

	return (
		<div>
			<Header
				profile={userProfile}
				setProfile={setUserProfile}
				selectedMode={RaceTypes.RANKED}
				selectedVehicle={null}
				title={t.tuning}
			/>

			<MoveZone carId={carId} />

			{getTuningInformationCard()}

			{tuning && getRoulette()}

			{isBuyConfirmOpen && (
				<ModuleWindow
					title={t.buy_tuning_title}
					text={getBuyConfirmText()}
					onYes={handleBuy}
					onNo={() => setIsBuyConfirmOpen(false)}
				/>
			)}

			<Toaster position='bottom-center' reverseOrder={false} />
		</div>
	)
}
